import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { MessageCircle, X, Send, Bot } from "lucide-react";

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Fliboe Support 🎙️ Ask me anything — sign in, station setup, plans, embeds, anything else." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/support/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "I'm having trouble reaching the support engine right now. Please try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black shadow-2xl shadow-emerald-500/40 flex items-center justify-center transition"
        aria-label="Open support chat"
        data-testid="support-chat-fab"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-44 right-6 z-40 w-[360px] max-w-[calc(100vw-2rem)] h-[480px] fl-card !p-0 flex flex-col shadow-2xl shadow-black/60" data-testid="support-chat-panel">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
              <Bot className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">Fliboe Support</div>
              <div className="fl-mono text-[9px] tracking-widest uppercase text-emerald-400 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Online · usually instant
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-white" aria-label="Close"><X className="w-4 h-4" /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="support-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-emerald-500 text-black rounded-br-sm"
                    : "bg-white/5 border border-white/10 text-slate-200 rounded-bl-sm"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl px-3.5 py-2 text-sm text-slate-400">
                  <span className="eq-bar" style={{ height: 8 }} /><span className="eq-bar" style={{ height: 8 }} /><span className="eq-bar" style={{ height: 8 }} />
                </div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex gap-2">
            <input
              className="fl-input text-sm"
              placeholder="Type your question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="support-chat-input"
              disabled={busy}
            />
            <button type="submit" disabled={busy || !input.trim()} className="fl-btn !py-2 !px-3" data-testid="support-chat-send" aria-label="Send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
